<template>
  <div>
    <b-card body-bg-variant="light"
            header="Additions to general part"
            header-bg-variant="dark"
            header-text-variant="light">

      <p class="p-form-label">Additional shifts </p>
      <p class="p-form-inline-description"> - Optional</p>
      <p class="p-form-description">
        If you want to make a request for more than maximum shifts allowed, please clarify your claim.
      </p>
      <b-form-textarea v-model="endStationParameters.additional_shifts"
                       :readonly="!editable"
                       :rows="3"
                       placeholder="Enter missing value"/>
    </b-card>

    <b-card body-bg-variant="light"
            header="General information"
            header-bg-variant="dark"
            header-text-variant="light">

      <p class="p-form-label">Detection modes</p>
      <extended-checkbox-group v-model="endStationParameters.detection_modes"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.detection_modes"
                               :options="detectionModesOptions"
                               :stacked="true"
                               class="detection_modes"/>

      <template v-if="endStationParameters.detection_modes && endStationParameters.detection_modes.length">
        <template v-if="isModeChosenInDetectionModes('In Expert Commissioning Access: Spin-ARPES')">
          <b-alert
            :show="true"
            class="alert"
            variant="danger">
            <h5 class="font-weight-bold mb-0">
              Remember that Spin-ARPES is currently available ONLY in Expert Commissioning Access mode.
              For more information visit this
              <a href="https://synchrotron.uj.edu.pl/en_GB/uzytkownicy/nabory-wnioskow"
                 rel="noopener noreferrer"
                 target="_blank">
                website</a>.
            </h5>
          </b-alert>
          <hr>
        </template>
      </template>

      <p class="p-form-label">Experiment type</p>
      <extended-checkbox-group v-model="endStationParameters.experiment_type"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.experiment_type"
                               :options="experimentTypeOptions"
                               :stacked="true"
                               class="detection_modes"/>

      <energy-range v-model="endStationParameters"
                    :beamline-specification="endStationSpecification"
                    :disabled="!editable"
                    :is_photon_energy_from_invalid="$v.endStationParameters.photon_energy_from"
                    :is_photon_energy_to_invalid="$v.endStationParameters.photon_energy_to"/>

      <p class="p-form-label">Photon polarisation</p>
      <extended-checkbox-group v-model="endStationParameters.photon_polarisation"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.photon_polarisation"
                               :options="photonPolarisationOptions"/>

      <temperature-range v-model="endStationParameters"
                         :beamline-specification="endStationSpecification.measurement"
                         :disabled="!editable"
                         :value_from_invalid="$v.endStationParameters.measurement_temperature_from"
                         :value_to_invalid="$v.endStationParameters.measurement_temperature_to">
      </temperature-range>

      <p class="p-form-label">Do you want to use cryogenic liquid?</p>
      <extended-checkbox-group v-model="endStationParameters.cryogenic_liquids"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.cryogenic_liquids"
                               :options="cryogenicLiquidsOptions"
                               unselectBtn="None"/>

      <template v-if="hasProperty(endStationParameters, 'cryogenic_liquids')
                  && endStationParameters.cryogenic_liquids.find(el => el === 'LHe')">
        <p class="p-form-label">Please clarify why do you need liquid helium</p>
        <p class="p-form-description">(max 300 characters incl. spaces)</p>
        <b-form-textarea v-model="endStationParameters.lhe_use_justification"
                         :class="{'custom-invalid-input-border custom-input-border-radius': $v.endStationParameters.lhe_use_justification.$error}"
                         placeholder="Enter missing value"
                         @input="$v.endStationParameters.lhe_use_justification.$touch()"/>
      </template>
    </b-card>

    <b-card body-bg-variant="light"
            header="Sample preparation and environment"
            header-bg-variant="dark"
            header-text-variant="light">

      <p class="p-form-label">Sample preparation</p>
      <extended-checkbox-group v-model="endStationParameters.sample_preparation"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.sample_preparation"
                               :options="samplePreparationOptions"/>

      <template v-if="hasProperty(endStationParameters, 'sample_preparation')
                  && endStationParameters.sample_preparation.find(el => el === 'In situ')">
        <p class="p-form-label">Heating</p>
        <extended-radio-group v-model="endStationParameters.heating"
                              :disabled="!editable"
                              :isInvalid="$v.endStationParameters.heating"
                              :options="yesNo"/>

        <temperature-range v-if="hasProperty(endStationParameters, 'heating') && endStationParameters.heating"
                           v-model="endStationParameters"
                           :beamline-specification="endStationSpecification.sample_preparation"
                           :disabled="!editable"
                           :label_text="'Temperature range'"
                           :value_from="'preparation_temperature_from'"
                           :value_from_invalid="$v.endStationParameters.preparation_temperature_from"
                           :value_to="'preparation_temperature_to'"
                           :value_to_invalid="$v.endStationParameters.preparation_temperature_to"/>
        <hr>
      </template>

      <p class="p-form-label">Evaporation</p>
      <extended-radio-group v-model="endStationParameters.evaporation"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.evaporation"
                            :options="yesNo"/>

      <template v-if="endStationParameters.evaporation">
        <two-data-list v-model="endStationParameters.evaporation_data"
                       :disabled="!editable"
                       :multiselect=true
                       :options="evaporationOptions"/>

        <p class="p-form-label">Evaporator</p>
        <extended-radio-group v-model="endStationParameters.evaporator"
                              :disabled="!editable"
                              :isInvalid="$v.endStationParameters.evaporator"
                              :options="evaporatorOptions"/>

        <two-data-list v-if="endStationParameters.evaporator === 'Own'"
                       v-model="endStationParameters.own_evaporator"
                       :disabled="!editable"
                       :multiselect=false
                       :options="ownEvaporatorOptions"/>
        <hr>
      </template>

      <p class="p-form-label">Ar-sputtering</p>
      <extended-radio-group v-model="endStationParameters.ar_sputtering"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.ar_sputtering"
                            :options="yesNo"/>

      <p class="p-form-label">Gas dosing</p>
      <extended-radio-group v-model="endStationParameters.gas_dosing"
                            :disabled="!editable"
                            :isInvalid="$v.endStationParameters.gas_dosing"
                            :options="yesNo"/>
      <two-data-list v-if="endStationParameters.gas_dosing"
                     v-model="endStationParameters.gas_dosing_data"
                     :disabled="!editable"
                     :options="gasDosingOptions"/>

      <p class="p-form-label">Detection methods</p>
      <extended-checkbox-group v-model="endStationParameters.detection_methods"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.detection_methods"
                               :options="detectionMethodsOptions"
                               unselectBtn="None of these"/>

      <p class="p-form-label">Inserting samples into the chamber</p>
      <extended-checkbox-group v-model="endStationParameters.inserting_to_chamber"
                               :disabled="!editable"
                               :isInvalid="$v.endStationParameters.inserting_to_chamber"
                               :options="insertingToChamberOptions"/>

      <template v-if="endStationParameters.inserting_to_chamber
                  && endStationParameters.inserting_to_chamber.includes('Vacuum suitcase')">

        <p class="p-form-label">Vacuum suitcase</p>
        <three-data-list v-model="endStationParameters.vacuum_suitcase"
                         :disabled="!editable"
                         :options="vacuumSuitcaseOptions"/>

      </template>
    </b-card>
  </div>
</template>

<script>
  import { endStationValidator } from '@/assets/validators/endStationBFormValidator';
  import ExtendedRadioGroup from '@/components/ExtendedRadioGroup';
  import energyRange from '@/components/proposal/proposals/energy-range';
  import temperatureRange from '@/components/proposal/proposals/temperature-range';
  import twoDataList from '@/components/two_data_list';
  import ExtendedCheckboxGroup from '@/components/ExtendedCheckboxGroup';
  import threeDataList from '@/components/three_data_list';
  import beamlineSpecification from '@/json/beamlineSpecification';
  import yesNo from '@/json/radioButtonYesNo';
  import {
    cryogenicLiquidsOptions,
    detectionMethodsOptions,
    detectionModesOptions,
    experimentTypeOptions,
    evaporationOptions,
    evaporatorOptions,
    gasDosingOptions,
    insertingToChamberOptions,
    ownEvaporatorOptions,
    photonPolarisationOptions,
    samplePreparationOptions,
    vacuumSuitcaseOptions,
  } from '@/helpers/proposal-form/endStationB';

  export default {
    name: 'EndStationBForm',
    components: {
      ExtendedRadioGroup,
      energyRange,
      temperatureRange,
      twoDataList,
      ExtendedCheckboxGroup,
      threeDataList,
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      editable: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        endStationParameters: this.value,
        yesNo,
        evaporationOptions,
        gasDosingOptions,
        photonPolarisationOptions,
        detectionModesOptions,
        experimentTypeOptions,
        cryogenicLiquidsOptions,
        samplePreparationOptions,
        evaporatorOptions,
        ownEvaporatorOptions,
        detectionMethodsOptions,
        insertingToChamberOptions,
        vacuumSuitcaseOptions,
        endStationSpecification: beamlineSpecification.endStationBSpecification,
      };
    },
    watch: {
      endStationParameters: {
        handler() {
          this.$emit('input', this.endStationParameters);
          this.$emit('beamline-validation', this.$v.endStationParameters);
        },
        deep: true,
      },
      'endStationParameters.additional_shifts': {
        handler() {
          if (!this.endStationParameters.additional_shifts) {
            delete this.endStationParameters.additional_shifts;
          }
        },
      },
      'endStationParameters.detection_modes': {
        handler() {
          this.detectionModesOptions
            .forEach((option) => {
              if (!this.endStationParameters.detection_modes.includes(option.name)) {
                delete this.endStationParameters[`${option.name.toLowerCase().replace('-', '')}_photon_energy_from`];
                delete this.endStationParameters[`${option.name.toLowerCase().replace('-', '')}_photon_energy_to`];
              }
            });
          if (this.endStationParameters.detection_modes.length === 0) {
            delete this.value.detection_modes;
          }
        },
      },
      'endStationParameters.cryogenic_liquids': {
        handler() {
          if (!this.isLHeChecked()) {
            delete this.endStationParameters.lhe_use_justification;
          }
        },
      },
      'endStationParameters.evaporation': {
        handler() {
          if (!this.endStationParameters.evaporation) {
            if (this.endStationParameters.evaporation_data) {
              delete this.endStationParameters.evaporation_data;
            }
            if (this.endStationParameters.evaporator) {
              delete this.endStationParameters.evaporator;
            }
          }
        },
      },
      'endStationParameters.evaporator': {
        handler() {
          if (!(this.endStationParameters.evaporator === 'Own') && this.endStationParameters.own_evaporator) {
            delete this.endStationParameters.own_evaporator;
          }
        },
      },
      'endStationParameters.gas_dosing': {
        handler() {
          if (!this.endStationParameters.gas_dosing && this.endStationParameters.gas_dosing_data) {
            delete this.endStationParameters.gas_dosing_data;
          }
        },
      },
      'endStationParameters.heating': {
        handler() {
          if (!this.endStationParameters.heating) {
            if (this.endStationParameters.preparation_temperature_from) {
              delete this.endStationParameters.preparation_temperature_from;
            }
            if (this.endStationParameters.preparation_temperature_to) {
              delete this.endStationParameters.preparation_temperature_to;
            }
          }
        },
      },
      'endStationParameters.inserting_to_chamber': {
        handler() {
          if (!this.endStationParameters.inserting_to_chamber.includes('Vacuum suitcase')
            && this.endStationParameters.vacuum_suitcase) {
            delete this.endStationParameters.vacuum_suitcase;
          }
        },
      },
      'endStationParameters.sample_preparation': {
        handler() {
          if (!this.endStationParameters.sample_preparation.includes('In situ')
            && this.endStationParameters.heating) {
            delete this.endStationParameters.heating;

            if (this.endStationParameters.preparation_temperature_from) {
              delete this.endStationParameters.preparation_temperature_from;
            }
            if (this.endStationParameters.preparation_temperature_to) {
              delete this.endStationParameters.preparation_temperature_to;
            }
          }
        },
      },
    },
    created() {
      this.$emit('beamline-validation', this.$v.endStationParameters);
    },
    methods: {
      isModeChosenInDetectionModes(mode) {
        return this.endStationParameters.detection_modes.includes(mode);
      },
      isLHeChecked() {
        return this.endStationParameters.cryogenic_liquids
          && this.endStationParameters.cryogenic_liquids.includes('LHe');
      },
    },
    validations: endStationValidator,
  };
</script>

<style scoped>
  .detection_modes {
    margin-bottom: 25px;
  }
</style>
